import { Button } from "@heroui/react";
import {
	type AdminCharacter,
	type AdminGoldenAsset,
	type CreateCharacterInput,
	type GenerationTargetType,
	GoldenAssetKind,
	GoldenAssetOwnerType,
	GenerationTargetType as TargetType,
	type UpdateCharacterInput,
} from "@storyline/shared";
import { useState } from "react";
import AssetRow from "@/src/series/components/asset-row";
import CharacterForm from "@/src/series/components/character-form";

const CHARACTER_KINDS = [GoldenAssetKind.CHARACTER_FACE, GoldenAssetKind.CHARACTER_ANGLE, GoldenAssetKind.CHARACTER_OUTFIT];

type CharacterCardProps = {
	character: AdminCharacter;
	assets: AdminGoldenAsset[];
	generatingKeys: Set<string>;
	onEdit: (characterId: string, body: UpdateCharacterInput) => Promise<void>;
	onRemove: (characterId: string) => Promise<void>;
	onGenerate: (targetType: GenerationTargetType, targetId: string, kind: GoldenAssetKind) => Promise<void>;
	onApprove: (assetId: string) => Promise<void>;
	onLock: (assetId: string) => Promise<void>;
	onUnlock: (assetId: string) => Promise<void>;
};

const CharacterCard: React.FC<CharacterCardProps> = ({
	character,
	assets,
	generatingKeys,
	onEdit,
	onRemove,
	onGenerate,
	onApprove,
	onLock,
	onUnlock,
}) => {
	const [editing, setEditing] = useState(false);

	const initial: CreateCharacterInput = {
		name: character.name,
		description: character.description,
		continuityRules: character.continuityRules,
		voiceNotes: character.voiceNotes,
	};

	return (
		<div className="flex flex-col gap-3 rounded-xl border border-border-soft bg-surface-2 p-4">
			<div className="flex items-start justify-between gap-3">
				<div className="flex flex-col gap-1">
					<span className="font-semibold text-foreground">{character.name}</span>
					<span className="text-muted text-sm">{character.description}</span>
					{character.continuityRules ? <span className="text-accent-lav text-xs">Continuité : {character.continuityRules}</span> : null}
				</div>
				<div className="flex shrink-0 gap-1">
					<Button size="sm" variant="ghost" onPress={() => setEditing((previous) => !previous)}>
						{editing ? "Fermer" : "Éditer"}
					</Button>
					<Button size="sm" variant="ghost" className="text-danger" onPress={() => void onRemove(character.id)}>
						Supprimer
					</Button>
				</div>
			</div>
			{editing ? (
				<CharacterForm
					initial={initial}
					submitLabel="Enregistrer"
					onSubmit={async (body) => {
						await onEdit(character.id, body);
						setEditing(false);
					}}
					onCancel={() => setEditing(false)}
				/>
			) : null}
			<AssetRow
				ownerType={GoldenAssetOwnerType.CHARACTER}
				ownerId={character.id}
				targetType={TargetType.CHARACTER}
				targetId={character.id}
				kinds={CHARACTER_KINDS}
				assets={assets}
				generatingKeys={generatingKeys}
				onGenerate={onGenerate}
				onApprove={onApprove}
				onLock={onLock}
				onUnlock={onUnlock}
			/>
		</div>
	);
};

export default CharacterCard;
